import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Spell } from "@dndModels";
import { SourceBookType } from "@dndModels/Types";

import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CheckBox from "@components/input/CheckBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import ViewSpell from "@dndComponents/display/ViewSpell";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CommonUtilities from '@utilities/common_utilities';

interface Props {
  initial_filter?: any;
  on_save?: () => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

const rows_per_page = 25;

type SortOption = "Name" | "Spell Level" | "School";

export default function SpellManager({
  initial_filter,
  on_save
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [filtered_spells, setFilteredSpells] = useState<Spell[]>([]);
  const [selected_spells, setSelectedSpells] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<any>(initial_filter || {});
  
  const [source_book_type, setSourceBookType] = useState<SourceBookType | "">("");
  const [source_book_id, setSourceBookId] = useState("");
  
  const [sort_by, setSortBy] = useState<SortOption>("Name");
  const [sort_ascending, setSortAscending] = useState(true);
  
  const [page, setPage] = useState(0);

  const sortSpells = useCallback((spells_to_sort: Spell[], use_sort_by: SortOption, asc: boolean) => {
    const sorted = [...spells_to_sort];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (use_sort_by) {
        case "Name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "Spell Level":
          comparison = a.level - b.level;
          // Secondary sort by name if levels are equal
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
        case "School":
          comparison = a.school.localeCompare(b.school);
          // Secondary sort by name if schools are equal
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
      }
      
      return asc ? comparison : -comparison;
    });
    
    return sorted;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const spell_objects = await api.getObjects({ data_type: "spell" }) as Spell[];
      setSpells(spell_objects);
      
      // Apply initial filter if provided
      let filtered: Spell[];
      if (initial_filter && Object.keys(initial_filter).length > 0) {
        filtered = api.filter("spell", spell_objects, initial_filter);
      } else {
        filtered = spell_objects;
      }
      
      // Apply sorting
      const sorted = sortSpells(filtered, sort_by, sort_ascending);
      setFilteredSpells(sorted);
    } catch (error) {
      console.error('Error loading spells:', error);
    }
    setLoading(false);
  }, [initial_filter, sortSpells, sort_by, sort_ascending]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  const handleFilterChange = useCallback((new_filters: any) => {
    // debugger;
    setFilters(new_filters);
    const filtered = api.filter("spell", spells, new_filters);
    const sorted = sortSpells(filtered, sort_by, sort_ascending);
    setFilteredSpells(sorted);
    setPage(0);
  }, [spells, sort_by, sort_ascending, sortSpells]);

  const handleSortChange = useCallback((new_sort: SortOption) => {
    // debugger;
    setSortBy(new_sort);
    const sorted = sortSpells(filtered_spells, new_sort, sort_ascending);
    setFilteredSpells(sorted);
    setPage(0);
  }, [filtered_spells, sort_ascending, sortSpells]);

  const handleToggleSortDirection = useCallback(() => {
    const asc = !sort_ascending;
    setSortAscending(asc);
    const sorted = sortSpells(filtered_spells, sort_by, asc);
    setFilteredSpells(sorted);
    setPage(0);
  }, [filtered_spells, sort_by, sort_ascending, sortSpells]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const new_selected = new Set<string>(
        filtered_spells
          .map(spell => spell._id)
      );
      setSelectedSpells(prev => {
        const combined = new Set(prev);
        new_selected.forEach(id => combined.add(id));
        return combined;
      });
    } else {
      setSelectedSpells(new Set());
    }
  };

  const handleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      const new_selected = new Set<string>(
        filtered_spells
          .slice(page * rows_per_page, page * rows_per_page + rows_per_page)
          .map(spell => spell._id)
      );
      setSelectedSpells(prev => {
        const combined = new Set(prev);
        new_selected.forEach(id => combined.add(id));
        return combined;
      });
    } else {
      const page_ids = new Set(
        filtered_spells
          .slice(page * rows_per_page, page * rows_per_page + rows_per_page)
          .map(spell => spell._id)
      );
      setSelectedSpells(prev => {
        const filtered = new Set<string>();
        prev.forEach(id => {
          if (!page_ids.has(id)) {
            filtered.add(id);
          }
        });
        return filtered;
      });
    }
  };

  const handleSelectSpell = (spell_id: string) => {
    setSelectedSpells(prev => {
      const new_set = new Set(prev);
      if (new_set.has(spell_id)) {
        new_set.delete(spell_id);
      } else {
        new_set.add(spell_id);
      }
      return new_set;
    });
  };

  const handleAssignSourceBook = async () => {
    if (selected_spells.size === 0) {
      alert('Please select at least one spell');
      return;
    }
    
    if (source_book_id === "") {
      alert('Please select a source book to assign to');
      return;
    }

    setSaving(true);
    try {
      const spell_ids = Array.from(selected_spells);
      const selected_spell_objects = spells.filter(s => spell_ids.includes(s._id));
      
      // Add new source books to existing ones
      const new_source_book = {
        type: source_book_type === "" ? "Source Book" : source_book_type,
        id: source_book_id,
        third_party: false
      };

      for (const spell of selected_spell_objects) {
        const existing_ids = spell.source_book_ids;
        if (!existing_ids.includes(source_book_id)) {
          // Check if it already has a variant in the source book.
          const variant = spell.variant_group.find(o => o.source_book_ids.includes(source_book_id));
          
          if (!variant) {
            spell.source_books_old.push(new_source_book);
          
            await api.updateObject("spell", spell);
          }
        }
      }
      
      // Reload spells to reflect changes
      await load();
      setSelectedSpells(new Set());
      alert(`Successfully assigned ${spell_ids.length} spell(s) to source book`);
      
      if (on_save) {
        on_save();
      }
    } catch (error) {
      console.error('Error assigning source book:', error);
      alert('Error assigning source book');
    }
    setSaving(false);
  };

  const handleNonVersionVariants = async () => {
    if (selected_spells.size === 0) {
      alert('Please select at least one spell');
      return;
    }

    setSaving(true);
    try {
      const spell_ids = Array.from(selected_spells);
      const selected_spell_objects = spells.filter(s => spell_ids.includes(s._id));
      
      let cleaned = 0;
      // for (const spell of selected_spell_objects) {
      //   if (spell.non_version_variant) {
      //     spell.variant_of_id = "";
      //     spell.variant_ids = [];
      //     spell.variant_group = [];
      //     // spell.non_version_variant = false;
      //     await api.updateObject("spell", spell);
      //     cleaned++;
      //   }
      // }
      
      // Reload spells to reflect changes
      await load();
      setSelectedSpells(new Set());
      alert(`Successfully cleaned Non-version Variant of ${cleaned} spell(s)`);
      
      if (on_save) {
        on_save();
      }
    } catch (error) {
      console.error('Error cleaning Non-version Variant:', error);
      alert('Error cleaning Non-version Variant');
    }
    setSaving(false);
  };

  const isSelected = (spell_id: string) => selected_spells.has(spell_id);

  const current_page_spells = filtered_spells.slice(
    page * rows_per_page,
    page * rows_per_page + rows_per_page
  );

  const all_page_selected = current_page_spells.length > 0 && 
    current_page_spells.every(spell => selected_spells.has(spell._id));

  if (loading || !fetched) {
    return <LoadingBox />;
  }

  return (
    <Grid container spacing={1}>
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Spell Source Book Manager
      </Grid>
      {/* Filter Section */}
      <Grid
        size={{
          xs: 12
        }}>
        <FilterBox
          data_type="spell"
          the_filter={filters}
          dont_use_api
          all_objects={spells}
          onChange={handleFilterChange}
        />
      </Grid>
      {/* Sort Section */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Sort Options
      </Grid>
      <Grid
        size={{
          xs: 8
        }}>
        <SelectStringBox
          name="Sort By"
          value={sort_by}
          options={["Name", "Spell Level", "School"]}
          onChange={handleSortChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <ButtonBox
          name={sort_ascending ? "Ascending ↑" : "Descending ↓"}
          onClick={handleToggleSortDirection}
        />
      </Grid>
      {/* Source Book Selection */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Assign to Source Book
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectSourceBookBox
          type={source_book_type}
          id={source_book_id}
          onChange={(type, id) => {
            setSourceBookType(type);
            setSourceBookId(id);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <ButtonBox
          name={`Assign ${selected_spells.size} Selected Spell(s)`}
          disabled={
            selected_spells.size === 0 || 
            source_book_id === "" ||
            saving
          }
          onClick={handleAssignSourceBook}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <ButtonBox
          name={`Clean Non-version variants for ${selected_spells.size} Selected`}
          disabled={selected_spells.size === 0 || saving}
          onClick={handleNonVersionVariants}
        />
      </Grid>
      {/* Spell List */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Spells ({filtered_spells.length} total, {selected_spells.size} selected)
      </Grid>
      {/* Select All Checkbox */}
      <Grid
        size={{
          xs: 12
        }}>
        <CheckBox
          name="Select ALL"
          value={selected_spells.size === filtered_spells.length && filtered_spells.length > 0}
          onChange={handleSelectAll}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <CheckBox
          name="Select All on Page"
          value={all_page_selected}
          onChange={handleSelectAllOnPage}
        />
      </Grid>
      {/* Spell Rows */}
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={0} direction="row" className="grid-striped">
          {current_page_spells.map((spell) => {
            const is_item_selected = isSelected(spell._id);
            const detail = spell.detailFormat(); // spell.detail_format; // char_util.getStringWithReplacementsWithObject({ clean_me: spell.detail_format, backup: "", obj: spell });
            return (
              <Grid
                key={spell._id}
                className="padded"
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row">
                  <Grid
                    className="centered"
                    size={{
                      xs: 1
                    }}>
                    <Checkbox 
                      checked={is_item_selected}
                      onChange={() => handleSelectSpell(spell._id)}
                    />
                  </Grid>
                  <Grid
                    className={is_item_selected ? "clickable selected" : "clickable"}
                    onClick={() => handleSelectSpell(spell._id)}
                    size={{
                      xs: 11
                    }}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <ViewSpell
                          spell={spell}
                          show_ritual
                          hide_details
                        />
                      </Grid>
                      { detail.length > 0 && 
                        <Grid
                          className="small_label gray"
                          size={{
                            xs: 12
                          }}>{ detail }</Grid> 
                      }
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      {current_page_spells.length === 0 && (
        <Grid
          className="centered"
          size={{
            xs: 12
          }}>
          No spells found
        </Grid>
      )}
      {/* Pagination */}
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={1}>
          <Grid
            size={{
              xs: 2
            }}>
            <ButtonBox
              name="Previous"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            />
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 8
            }}>
            Page {page + 1} of {Math.ceil(filtered_spells.length / rows_per_page)}
            {' '}(Showing {page * rows_per_page + 1}-{Math.min((page + 1) * rows_per_page, filtered_spells.length)} of {filtered_spells.length})
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <ButtonBox
              name="Next"
              disabled={(page + 1) * rows_per_page >= filtered_spells.length}
              onClick={() => setPage(page + 1)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}